import { useMemo, useState } from "react";
import { Link, router } from "@inertiajs/react";
import { Search, Sprout } from "lucide-react";
import { ListTemplate } from "@/templates/ListTemplate";
import { Table } from "@/components/Table";
import { EmptyState } from "@/components/EmptyState";
import { Input } from "@/components/Input";

interface ProjectRow {
  id: string;
  objId: string;
  name: string;
  goalsCount: number;
  contributionsCount: number;
  contributorCount: number;
  totalAllocatedMinor: number;
  lastContributedAt: string | null;
}

interface Props {
  projects: ProjectRow[];
}

function fmtMoneyCompact(minor: number, currency = "£") {
  const major = minor / 100;
  if (Math.abs(major) >= 1000)
    return `${currency}${(major / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })}K`;
  return `${currency}${major.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function fmtRelative(iso: string | null) {
  if (!iso) return "—";
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (d <= 0) return "today";
  if (d === 1) return "yesterday";
  if (d < 30) return `${d}d ago`;
  if (d < 365) return `${Math.floor(d / 30)}mo ago`;
  return `${Math.floor(d / 365)}y ago`;
}

export default function ProjectsIndex({ projects }: Props) {
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return projects;
    return projects.filter(
      (p) => p.name.toLowerCase().includes(s) || p.objId.toLowerCase().includes(s),
    );
  }, [projects, q]);

  return (
    <ListTemplate
      title="Projects"
      description="The nature projects you supply, and the contributions allocated to each."
      breadcrumbs={[{ label: "Home", to: "/" }, { label: "Projects" }]}
      meta={
        <span className="hidden sm:inline-flex items-center text-sm text-fg-muted tabular">
          <span className="font-medium text-fg">{projects.length}</span>
          <span className="ml-1">projects</span>
        </span>
      }
      filters={
        <div className="w-full sm:w-72">
          <Input
            leadingIcon={<Search />}
            placeholder="Search projects…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      }
    >
      {filtered.length === 0 ? (
        <EmptyState
          icon={<Sprout />}
          title={q ? "No projects match" : "No projects yet"}
          description={q ? "Try a different search." : "Projects shared with you will appear here."}
        />
      ) : (
        <Table>
          <Table.Header>
            <tr>
              <Table.Head className="pl-4">Project</Table.Head>
              <Table.Head className="text-right">Goals</Table.Head>
              <Table.Head className="text-right">Contributions</Table.Head>
              <Table.Head className="text-right">Contributors</Table.Head>
              <Table.Head className="text-right">Allocated</Table.Head>
              <Table.Head>Last activity</Table.Head>
            </tr>
          </Table.Header>
          <Table.Body>
            {filtered.map((p) => {
              const url = `/projects/${p.id}`;
              return (
                <Table.Row
                  key={p.id}
                  className="cursor-pointer"
                  onClick={() => router.visit(url)}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") router.visit(url);
                  }}
                >
                  <Table.Cell className="pl-4">
                    <div className="flex items-center gap-2.5" onClick={(e) => e.stopPropagation()}>
                      <span className="flex size-7 shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-accent-subtle text-accent-text [&_svg]:size-4">
                        <Sprout />
                      </span>
                      <div className="min-w-0">
                        <Link
                          href={url}
                          className="block truncate font-medium text-fg hover:text-accent-text transition-colors"
                        >
                          {p.name}
                        </Link>
                        <div className="truncate font-mono text-xs text-fg-muted">{p.objId}</div>
                      </div>
                    </div>
                  </Table.Cell>
                  <Table.Cell className="text-right tabular text-fg-muted">
                    {p.goalsCount.toLocaleString()}
                  </Table.Cell>
                  <Table.Cell className="text-right tabular text-fg-muted">
                    {p.contributionsCount.toLocaleString()}
                  </Table.Cell>
                  <Table.Cell className="text-right tabular text-fg-muted">
                    {p.contributorCount.toLocaleString()}
                  </Table.Cell>
                  <Table.Cell className="text-right tabular font-medium text-fg">
                    {fmtMoneyCompact(p.totalAllocatedMinor)}
                  </Table.Cell>
                  <Table.Cell className="text-fg-muted tabular whitespace-nowrap">
                    {fmtRelative(p.lastContributedAt)}
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      )}
    </ListTemplate>
  );
}
